/**
 * Settings Page
 * ==============
 * صفحة الإعدادات
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTenantAuth } from '../../context/TenantAuthContext';
import { useToast } from '../../context/ToastContext';
import '../shared/PageStyles.css';
import SAR from '../../../components/SAR';

const I = {
  save: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>,
  user: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  bell: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>,
  lock: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>,
  dollar: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
  clock: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  building: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-4"/><path d="M9 9h1M9 13h1M9 17h1"/></svg>,
  eye: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
  eyeOff: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>,
};

const SettingsPage = () => {
  const { apiCall, user, updateProfile } = useTenantAuth();
  const { success, error: showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [showPassword, setShowPassword] = useState(false);
  
  const [profile, setProfile] = useState({ name: '', email: '', phone: '', logo_url: '' });
  const [passwordData, setPasswordData] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [notifications, setNotifications] = useState({ email_orders: true, email_support: true, sms_orders: false, push_enabled: true });
  const [pricing, setPricing] = useState({ base_price_per_ton: '', delivery_fee: '', min_order_quantity: '' });
  const [hours, setHours] = useState({ open_time: '08:00', close_time: '22:00', is_24_hours: false, working_days: [0, 1, 2, 3, 4, 5] });

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const [profileData, notifData, pricingData, hoursData] = await Promise.all([
        apiCall('/tenant/settings/profile'),
        apiCall('/tenant/settings/notifications'),
        apiCall('/tenant/settings/pricing'),
        apiCall('/tenant/settings/hours')
      ]);
      
      setProfile(profileData || {});
      setNotifications(notifData || {});
      setPricing(pricingData || {});
      setHours(hoursData || {});
    } catch (err) {
      showError('فشل تحميل الإعدادات');
    } finally {
      setLoading(false);
    }
  }, [apiCall, showError]);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const saveProfile = async () => {
    setSaving(true);
    try {
      await apiCall('/tenant/settings/profile', { method: 'PUT', body: JSON.stringify(profile) });
      if (updateProfile) updateProfile(profile);
      success('تم حفظ معلومات الشركة');
    } catch (err) {
      showError(err.message || 'فشل حفظ المعلومات');
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      showError('كلمات المرور غير متطابقة');
      return;
    }
    if (passwordData.new_password.length < 8) {
      showError('كلمة المرور يجب أن تكون 8 أحرف على الأقل');
      return;
    }
    
    setSaving(true);
    try {
      await apiCall('/tenant/auth/change-password', {
        method: 'PUT',
        body: JSON.stringify({ current_password: passwordData.current_password, new_password: passwordData.new_password })
      });
      success('تم تغيير كلمة المرور');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      showError(err.message || 'فشل تغيير كلمة المرور');
    } finally {
      setSaving(false);
    }
  };

  const saveNotifications = async () => {
    setSaving(true);
    try {
      await apiCall('/tenant/settings/notifications', { method: 'PUT', body: JSON.stringify(notifications) });
      success('تم حفظ إعدادات الإشعارات');
    } catch (err) {
      showError('فشل حفظ الإعدادات');
    } finally {
      setSaving(false);
    }
  };

  const savePricing = async () => {
    setSaving(true);
    try {
      await apiCall('/tenant/settings/pricing', { method: 'PUT', body: JSON.stringify(pricing) });
      success('تم حفظ إعدادات التسعير');
    } catch (err) {
      showError('فشل حفظ الإعدادات');
    } finally {
      setSaving(false);
    }
  };

  const saveHours = async () => {
    setSaving(true);
    try {
      await apiCall('/tenant/settings/hours', { method: 'PUT', body: JSON.stringify(hours) });
      success('تم حفظ ساعات العمل');
    } catch (err) {
      showError('فشل حفظ الإعدادات');
    } finally {
      setSaving(false);
    }
  };

  const days = ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];

  if (loading) {
    return <div className="page-loading"><div className="spinner" /></div>;
  }

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header__title">
          <h1>الإعدادات</h1>
          <p>إدارة إعدادات الشركة</p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: '24px' }}>
        {/* Sidebar */}
        <div className="card" style={{ padding: '8px', height: 'fit-content' }}>
          {[
            { id: 'profile', icon: I.building, label: 'معلومات الشركة' },
            { id: 'security', icon: I.lock, label: 'الأمان' },
            { id: 'notifications', icon: I.bell, label: 'الإشعارات' },
            { id: 'pricing', icon: I.dollar, label: 'التسعير' },
            { id: 'hours', icon: I.clock, label: 'ساعات العمل' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 16px',
                border: 'none',
                background: activeTab === tab.id ? 'var(--tenant-primary)' : 'transparent',
                color: activeTab === tab.id ? 'white' : 'var(--tenant-text)',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: activeTab === tab.id ? '500' : '400',
                marginBottom: '4px'
              }}
            >
              <span style={{ width: '20px', height: '20px' }}>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="card">
          {activeTab === 'profile' && (
            <>
              <div className="card-header"><div className="card-title">معلومات الشركة</div></div>
              <div style={{ padding: '24px' }}>
                <div className="form-grid">
                  <div className="form-group"><label>اسم الشركة</label><input type="text" value={profile.name || ''} onChange={(e) => setProfile({...profile, name: e.target.value})} /></div>
                  <div className="form-group"><label>البريد الإلكتروني</label><input type="email" value={profile.email || ''} onChange={(e) => setProfile({...profile, email: e.target.value})} /></div>
                  <div className="form-group"><label>رقم الهاتف</label><input type="tel" value={profile.phone || ''} onChange={(e) => setProfile({...profile, phone: e.target.value})} /></div>
                  <div className="form-group"><label>رابط الشعار</label><input type="url" value={profile.logo_url || ''} onChange={(e) => setProfile({...profile, logo_url: e.target.value})} /></div>
                  <div className="form-group full-width"><label>العنوان</label><input type="text" value={profile.address || ''} onChange={(e) => setProfile({...profile, address: e.target.value})} /></div>
                  <div className="form-group full-width"><label>الوصف</label><textarea value={profile.description || ''} onChange={(e) => setProfile({...profile, description: e.target.value})} /></div>
                </div>
                <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
                  <button className="btn btn--primary" onClick={saveProfile} disabled={saving}>{I.save} <span>{saving ? 'جاري الحفظ...' : 'حفظ'}</span></button>
                </div>
              </div>
            </>
          )}

          {activeTab === 'security' && (
            <>
              <div className="card-header"><div className="card-title">تغيير كلمة المرور</div></div>
              <div style={{ padding: '24px' }}>
                <div className="form-grid" style={{ maxWidth: '400px' }}>
                  <div className="form-group full-width">
                    <label>كلمة المرور الحالية</label>
                    <div style={{ position: 'relative' }}>
                      <input type={showPassword ? 'text' : 'password'} value={passwordData.current_password} onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})} />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--tenant-text-muted)' }}>
                        {showPassword ? I.eyeOff : I.eye}
                      </button>
                    </div>
                  </div>
                  <div className="form-group full-width"><label>كلمة المرور الجديدة</label><input type={showPassword ? 'text' : 'password'} value={passwordData.new_password} onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})} /></div>
                  <div className="form-group full-width"><label>تأكيد كلمة المرور</label><input type={showPassword ? 'text' : 'password'} value={passwordData.confirm_password} onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})} /></div>
                </div>
                <div style={{ marginTop: '24px' }}>
                  <button className="btn btn--primary" onClick={changePassword} disabled={saving}>{saving ? 'جاري التغيير...' : 'تغيير كلمة المرور'}</button>
                </div>
              </div>
            </>
          )}

          {activeTab === 'notifications' && (
            <>
              <div className="card-header"><div className="card-title">إعدادات الإشعارات</div></div>
              <div style={{ padding: '24px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {[
                    { key: 'email_orders', label: 'إشعارات الطلبات بالبريد' },
                    { key: 'email_support', label: 'إشعارات الدعم الفني بالبريد' },
                    { key: 'sms_orders', label: 'إشعارات الطلبات بالرسائل النصية' },
                    { key: 'push_enabled', label: 'الإشعارات الفورية' },
                  ].map(item => (
                    <label key={item.key} style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={notifications[item.key] || false} onChange={(e) => setNotifications({...notifications, [item.key]: e.target.checked})} style={{ width: '20px', height: '20px' }} />
                      <span>{item.label}</span>
                    </label>
                  ))}
                </div>
                <div style={{ marginTop: '24px' }}>
                  <button className="btn btn--primary" onClick={saveNotifications} disabled={saving}>{I.save} <span>{saving ? 'جاري الحفظ...' : 'حفظ'}</span></button>
                </div>
              </div>
            </>
          )}

          {activeTab === 'pricing' && (
            <>
              <div className="card-header"><div className="card-title">إعدادات التسعير</div></div>
              <div style={{ padding: '24px' }}>
                <div className="form-grid">
                  <div className="form-group"><label>السعر الأساسي للطن (<SAR className="h-[0.8em] w-auto" />)</label><input type="number" step="0.01" value={pricing.base_price_per_ton || ''} onChange={(e) => setPricing({...pricing, base_price_per_ton: e.target.value})} /></div>
                  <div className="form-group"><label>رسوم التوصيل (<SAR className="h-[0.8em] w-auto" />)</label><input type="number" step="0.01" value={pricing.delivery_fee || ''} onChange={(e) => setPricing({...pricing, delivery_fee: e.target.value})} /></div>
                  <div className="form-group"><label>الحد الأدنى للطلب (طن)</label><input type="number" step="0.1" value={pricing.min_order_quantity || ''} onChange={(e) => setPricing({...pricing, min_order_quantity: e.target.value})} /></div>
                </div>
                <div style={{ marginTop: '24px' }}>
                  <button className="btn btn--primary" onClick={savePricing} disabled={saving}>{I.save} <span>{saving ? 'جاري الحفظ...' : 'حفظ'}</span></button>
                </div>
              </div>
            </>
          )}

          {activeTab === 'hours' && (
            <>
              <div className="card-header"><div className="card-title">ساعات العمل</div></div>
              <div style={{ padding: '24px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={hours.is_24_hours || false} onChange={(e) => setHours({...hours, is_24_hours: e.target.checked})} style={{ width: '20px', height: '20px' }} />
                  <span>متاح 24 ساعة</span>
                </label>
                
                {!hours.is_24_hours && (
                  <div className="form-grid" style={{ marginBottom: '20px' }}>
                    <div className="form-group"><label>وقت الفتح</label><input type="time" value={hours.open_time || ''} onChange={(e) => setHours({...hours, open_time: e.target.value})} /></div>
                    <div className="form-group"><label>وقت الإغلاق</label><input type="time" value={hours.close_time || ''} onChange={(e) => setHours({...hours, close_time: e.target.value})} /></div>
                  </div>
                )}

                <div>
                  <label style={{ display: 'block', marginBottom: '12px', fontWeight: '500' }}>أيام العمل</label>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {days.map((day, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => {
                          const working = hours.working_days || [];
                          setHours({...hours, working_days: working.includes(i) ? working.filter(d => d !== i) : [...working, i]});
                        }}
                        style={{
                          padding: '8px 16px',
                          border: '1px solid var(--tenant-border)',
                          borderRadius: '8px',
                          background: (hours.working_days || []).includes(i) ? 'var(--tenant-primary)' : 'white',
                          color: (hours.working_days || []).includes(i) ? 'white' : 'var(--tenant-text)',
                          cursor: 'pointer'
                        }}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div style={{ marginTop: '24px' }}>
                  <button className="btn btn--primary" onClick={saveHours} disabled={saving}>{I.save} <span>{saving ? 'جاري الحفظ...' : 'حفظ'}</span></button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
